import { useMemo } from 'react';
import { useSignal, initData } from '@tma.js/sdk-react';
import { en } from './translations/en';
import { uk } from './translations/uk';
import { useUserPreferences } from '../contexts/UserPreferencesContext';

export type Language = 'en' | 'uk';

const translations = {
  en,
  uk,
};

const defaultLanguage: Language = 'uk';

/**
 * Creates a proxy that intercepts property access and returns [key] for missing translations
 * This is a pure function that doesn't depend on React hooks, so it's defined outside the hook
 * Returns `any` to allow flexible property access since translations can have any structure
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function createTranslationProxy(obj: Record<string, unknown>, path: string = ''): any {
  return new Proxy(obj, {
    get(target, prop: string | symbol) {
      // Handle special properties
      if (typeof prop === 'symbol' || prop === 'toJSON' || prop === 'constructor') {
        return target[prop as keyof typeof target];
      }

      const propKey = String(prop);
      const fullPath = path ? `${path}.${propKey}` : propKey;
      const value = target[propKey];

      if (value === undefined) {
        console.warn(`Translation key not found: ${fullPath}`);
        // Return a string that will be displayed in UI
        // When used in JSX, React will convert it to string automatically
        return `[${fullPath}]`;
      }

      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        // Recursively create proxy for nested objects
        return createTranslationProxy(value as Record<string, unknown>, fullPath);
      }

      return value;
    },
  });
}

/**
 * Hook to get translations based on user's language
 * Uses Telegram user language or falls back to default
 */
export function useTranslation() {
  const initDataState = useSignal(initData.state);
  const { relationshipType } = useUserPreferences();
  
  // Get language from Telegram user data or use default
  const language: Language = useMemo(() => {
    const userLanguage = initDataState?.user?.language_code;
    
    // Map language codes to supported languages
    if (userLanguage === 'uk' || userLanguage === 'ua') return 'uk';
    if (userLanguage === 'en') return 'en';
    
    return defaultLanguage;
  }, [initDataState]);

  // Get base translations (no overrides needed)
  const t = useMemo(() => {
    const baseTranslations = translations[language];
    
    // Wrap translations in a proxy to handle missing keys
    // Using `any` type to allow flexible property access since translations can have any structure
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return createTranslationProxy(baseTranslations) as any;
  }, [language]);

  /**
   * Translate a key with optional variable replacement
   * @param key - Translation key (e.g., 'dashboard.welcome')
   * @param vars - Variables to replace in the translation (e.g., { name: 'John' })
   */
  const translate = (key: string, vars?: Record<string, string | number>): string => {
    const keys = key.split('.');
    let value: Record<string, unknown> | string = t;

    // Navigate through nested object
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k] as Record<string, unknown> | string;
      } else {
        console.warn(`Translation key not found: ${key}`);
        // Return the key formatted for display (e.g., "dashboard.welcome" -> "dashboard.welcome")
        return `[${key}]`;
      }
    }

    if (typeof value !== 'string') {
      console.warn(`Translation key is not a string: ${key}`);
      // Return the key formatted for display
      return `[${key}]`;
    }

    // Replace variables in the format {{varName}}
    if (vars) {
      return value.replace(/\{\{(\w+)\}\}/g, (match, varName) => {
        return varName in vars ? String(vars[varName]) : match;
      });
    }

    return value;
  };

  return {
    t,
    translate,
    language,
    relationshipType,
  };
}
